import type { ReactNode } from "react";
import type { PanelProps } from "../shared";
import { ClockPanel } from "./clock-panel";
import { ClosingPanel } from "./closing-panel";
import { FilesPanel } from "./files-panel";
import { GuideStepsPanel } from "./guide-steps-panel";
import { MessagesPanel } from "./messages-panel";
import { MetaPanel } from "./meta-panel";
import {
  AirbnbPanel,
  MapsPanel,
  PaypalPanel,
  RemindersPanel,
  TinderPanel,
} from "./nested-apps-panel";
import { CastPanel, PeoplePanel } from "./people-panel";
import { PhoneCallsPanel } from "./phone-calls-panel";
import { PhotosPanel } from "./photos-panel";
import { PuzzleStepsPanel } from "./puzzle-steps-panel";
import { RelationshipsPanel } from "./relationships-panel";
import {
  CalendarPanel,
  GmailPanel,
  HealthPanel,
  NotesPanel,
  SafariBookmarksPanel,
  SafariPanel,
  SocialFeedPanel,
  VoiceMemosPanel,
} from "./text-records-panel";
import { TikTokPanel } from "./tiktok-panel";
import { UberPanel } from "./uber-panel";
import { WorldPanel } from "./world-panel";

/** Resolve a specialized ops panel for a section path, or null for generic form. */
export function renderSpecialPanel(
  sectionPath: string,
  props: PanelProps,
): ReactNode | null {
  switch (sectionPath) {
    case "meta":
      return <MetaPanel {...props} />;
    case "game.cast":
      return <CastPanel {...props} />;
    case "game.guideSteps":
      return <GuideStepsPanel {...props} />;
    case "game.puzzleSteps":
      return <PuzzleStepsPanel {...props} />;
    case "game.closing":
      return <ClosingPanel {...props} />;
    case "canon.people":
      return <PeoplePanel {...props} />;
    case "canon.relationships":
      return <RelationshipsPanel {...props} />;
    case "canon.world":
      return <WorldPanel {...props} />;
    case "device.apps.messages":
      return <MessagesPanel {...props} title="短信" />;
    case "device.apps.messages_recently_deleted":
      return (
        <MessagesPanel
          {...props}
          appPath="device.apps.messages_recently_deleted"
          title="最近删除短信"
        />
      );
    case "device.apps.whatsapp":
      return (
        <MessagesPanel
          {...props}
          appPath="device.apps.whatsapp"
          title="WhatsApp"
          supportsGroups
          supportsImages
        />
      );
    case "device.apps.phone":
      return <PhoneCallsPanel {...props} />;
    case "device.apps.photos":
      return <PhotosPanel {...props} />;
    case "device.apps.notes":
      return <NotesPanel {...props} />;
    case "device.apps.files":
      return <FilesPanel {...props} />;
    case "device.apps.tiktok":
      return <TikTokPanel {...props} />;
    case "device.apps.uber":
      return <UberPanel {...props} />;
    case "device.apps.gmail":
      return <GmailPanel {...props} />;
    case "device.apps.safari":
      return <SafariPanel {...props} />;
    case "device.apps.safari_bookmarks":
      return <SafariBookmarksPanel {...props} />;
    case "device.apps.calendar":
      return <CalendarPanel {...props} />;
    case "device.apps.maps":
      return <MapsPanel {...props} />;
    case "device.apps.clock":
      return <ClockPanel {...props} />;
    case "device.apps.instagram":
      return (
        <SocialFeedPanel
          {...props}
          path="device.apps.instagram"
          title="Instagram"
        />
      );
    case "device.apps.facebook":
      return (
        <SocialFeedPanel
          {...props}
          path="device.apps.facebook"
          title="Facebook"
        />
      );
    case "device.apps.voice-memos":
      return <VoiceMemosPanel {...props} />;
    case "device.apps.health":
      return <HealthPanel {...props} />;
    case "device.apps.tinder":
      return <TinderPanel {...props} />;
    case "device.apps.reminders":
      return <RemindersPanel {...props} />;
    case "device.apps.airbnb":
      return <AirbnbPanel {...props} />;
    case "device.apps.paypal":
      return <PaypalPanel {...props} />;
    default:
      return null;
  }
}
